import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";

import { listCardProductsRequest } from "../../../../utils/api";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  maxWidth: "95%",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const AddOrgCardModal = ({ open, handleClose, onSubmit, isSubmitting = false, orgCode = "" }) => {
  const [formData, setFormData] = useState({
    orgCode: orgCode || "",
    productCodeUuid: "",
  });

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Dropdown state
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState(null);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (open) {
      resetForm();
      loadProductDropdown("");
    }
  }, [open]);

  const resetForm = () => {
    setFormData({ orgCode: orgCode || "", productCodeUuid: "" });
    setAutocompleteValue(null);
    setInputValue("");
    setConfirmOpen(false);
  };

  // Load dropdown options
  const loadProductDropdown = async (searchText = "") => {
    try {
      setLoadingProducts(true);

      const res = await listCardProductsRequest({
        productId: searchText || null,
        pageNo: 0,
        pageSize: 50,
      });

      const content = res?.data?.data?.content ?? [];

      // IMPORTANT FIX — productId = id (UUID), display productCode
      const opts = content.map((c) => ({
        id: c.id, // UUID for backend
        productId: c.id, // treat id as productId
        displayCode: `${c.productName}-${c.productCode}`, // show only productCode in dropdown
      }));

      setProductOptions(opts);
    } catch (e) {
      console.error("Failed to load product list", e);
      setProductOptions([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Org Code
  const handleOrgCodeChange = (e) => {
    setFormData((prev) => ({ ...prev, orgCode: e.target.value }));
  };

  // When user selects an option
  const handleSelectProduct = (event, newValue) => {
    setAutocompleteValue(newValue);

    setFormData((prev) => ({
      ...prev,
      productCodeUuid: newValue ? newValue.id : "", // send UUID
    }));
  };

  // Search inside dropdown
  const handleAutocompleteInputChange = (event, newValue) => {
    setInputValue(newValue);
    loadProductDropdown(newValue);
  };

  const isFormValid = formData.orgCode.trim() !== "" && Boolean(formData.productCodeUuid);

  const submit = (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (onSubmit) await onSubmit(formData);
    setConfirmOpen(false);
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "rgba(0,0,0,0.87)" },
    "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "rgba(0,0,0,0.87)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "rgba(0,0,0,0.87)" },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": { color: "rgba(0,0,0,0.87)" },
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6">Add Org Card</Typography>

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* ORG CODE */}
            <TextField
              label="Org Code"
              name="orgCode"
              value={formData.orgCode}
              onChange={handleOrgCodeChange}
              required
              fullWidth
              disabled={Boolean(orgCode) || isSubmitting}
              sx={textFieldSx}
              placeholder="Enter organization code (e.g., EPS)"
            />

            {/* PRODUCT ID DROPDOWN */}
            <Autocomplete
              value={autocompleteValue}
              onChange={handleSelectProduct}
              inputValue={inputValue}
              onInputChange={handleAutocompleteInputChange}
              options={productOptions}
              getOptionLabel={(option) => option?.displayCode?.toString() || ""}
              filterOptions={(x) => x}
              loading={loadingProducts}
              disabled={isSubmitting}
              noOptionsText={loadingProducts ? "Loading..." : "No product found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product ID"
                  required
                  fullWidth
                  sx={textFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* SUBMIT */}
            <Button
              type="submit"
              variant="contained"
              disabled={!isFormValid || isSubmitting}
              sx={{
                mt: 1,
                backgroundColor: isFormValid && !isSubmitting ? "#ED141F" : "rgba(237,20,31,0.5)",
                "&:hover": {
                  backgroundColor: isFormValid && !isSubmitting ? "#C21014" : "rgba(237,20,31,0.5)",
                },
              }}
            >
              {isSubmitting ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CircularProgress size={20} color="inherit" />
                  Submitting...
                </span>
              ) : (
                "Add Org Card"
              )}
            </Button>
          </form>
        </Box>
      </Modal>

      {/* CONFIRMATION POPUP */}
      <Dialog open={confirmOpen} onClose={handleClose}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <Typography>
            Create org card for <strong>{formData.orgCode}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isSubmitting} sx={{ color: "#ED141F" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#ED141F",
              "&:hover": { backgroundColor: "#C21014" },
            }}
          >
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddOrgCardModal;
